/**
 * Round diff: compare prev/next snapshots and produce domain diff events.
 * Normalizes to stable domain events (PartiesChanged, BetsAdded, etc.).
 */

import type {
  RoundSnapshot,
  RoundParty,
  SnapshotBet,
  AppliedTrick,
  RoundTiming,
} from "@/shared/api/types/pvpRoulette"

export type DomainDiffEvent =
  | VersionAdvanced
  | StatusChanged
  | PartiesChanged
  | BetsAdded
  | BetsUpdated
  | BetsRemoved
  | TrickAdded
  | TrickUpdated
  | TrickRemoved
  | TimingChanged
  | TargetCoordinateChanged

export interface VersionAdvanced {
  type: "VersionAdvanced"
  prevVersion: number
  nextVersion: number
}

export interface StatusChanged {
  type: "StatusChanged"
  prev: string
  next: string
}

export interface PartiesChanged {
  type: "PartiesChanged"
  parties: RoundParty[]
  totalBankTon?: string
}

export interface BetsAdded {
  type: "BetsAdded"
  bets: SnapshotBet[]
}

export interface BetsUpdated {
  type: "BetsUpdated"
  bets: SnapshotBet[]
}

export interface BetsRemoved {
  type: "BetsRemoved"
  betIds: string[]
}

export interface TrickAdded {
  type: "TrickAdded"
  trick: AppliedTrick
}

export interface TrickUpdated {
  type: "TrickUpdated"
  trick: AppliedTrick
}

export interface TrickRemoved {
  type: "TrickRemoved"
  trickId: string
}

export interface TimingChanged {
  type: "TimingChanged"
  timing: RoundTiming
}

export interface TargetCoordinateChanged {
  type: "TargetCoordinateChanged"
  prevCentideg: number
  nextCentideg: number
}

const EMPTY_SNAPSHOT: RoundSnapshot = {
  version: 0,
  roundId: "",
  roundNum: 0,
  status: "WAITING",
  mode: "SOLO",
  parties: [],
  bets: [],
  tricks: [],
  timing: {},
  targetCoordinate: 0,
  prizePoolTon: "0",
  allowedTricks: [],
}

/**
 * Compute domain diff events between prev and next snapshots.
 * Stable output order: version, status, parties, bets, tricks, timing, target.
 */
export function roundDiff(
  prev: RoundSnapshot | null,
  next: RoundSnapshot
): DomainDiffEvent[] {
  const events: DomainDiffEvent[] = []
  const p = prev ?? EMPTY_SNAPSHOT

  if (next.version !== p.version) {
    events.push({
      type: "VersionAdvanced",
      prevVersion: p.version,
      nextVersion: next.version,
    })
  }

  if (next.status !== p.status) {
    events.push({ type: "StatusChanged", prev: p.status, next: next.status })
  }

  if (!shallowEqualArrays(p.parties, next.parties, partyKey)) {
    events.push({ type: "PartiesChanged", parties: next.parties })
  }

  const betsDiff = arrayDiff(p.bets, next.bets, (b) => b.betId)
  if (betsDiff.added.length > 0) {
    events.push({ type: "BetsAdded", bets: betsDiff.added })
  }
  if (betsDiff.updated.length > 0) {
    events.push({ type: "BetsUpdated", bets: betsDiff.updated })
  }
  if (betsDiff.removed.length > 0) {
    events.push({ type: "BetsRemoved", betIds: betsDiff.removed })
  }

  // Key by (type, userId) only: optimistic tricks are added to rawRound without
  // instanceId, so including it would diff the optimistic prev trick vs the
  // WS-echo next trick as different items and emit a duplicate ApplyTrick*.
  const trickKey = (t: AppliedTrick) => `${t.type}:${t.userId}`
  const tricksDiff = arrayDiff(p.tricks, next.tricks, trickKey)
  if (tricksDiff.added.length === 1) {
    events.push({ type: "TrickAdded", trick: tricksDiff.added[0] })
  } else if (tricksDiff.added.length > 1) {
    tricksDiff.added.forEach((t) =>
      events.push({ type: "TrickAdded", trick: t })
    )
  }
  if (tricksDiff.updated.length > 0) {
    tricksDiff.updated.forEach((t) =>
      events.push({ type: "TrickUpdated", trick: t })
    )
  }
  if (tricksDiff.removed.length > 0) {
    tricksDiff.removed.forEach((key) =>
      events.push({ type: "TrickRemoved", trickId: key })
    )
  }

  if (!shallowEqualTiming(p.timing, next.timing)) {
    events.push({ type: "TimingChanged", timing: next.timing })
  }

  const nextTarget = next.targetCoordinate ?? next.roulettePosition ?? 0
  const prevTarget = p.targetCoordinate ?? p.roulettePosition ?? 0
  if (nextTarget !== prevTarget) {
    events.push({
      type: "TargetCoordinateChanged",
      prevCentideg: prevTarget,
      nextCentideg: nextTarget,
    })
  }

  return events
}

function partyKey(party: RoundParty): string {
  return party.id ?? party.partyId ?? party.userId
}

function shallowEqualArrays<T>(a: T[], b: T[], key: (t: T) => string): boolean {
  if (a.length !== b.length) return false
  const mapA = new Map(a.map((t) => [key(t), t]))
  for (const bItem of b) {
    const aItem = mapA.get(key(bItem))
    if (!aItem || JSON.stringify(aItem) !== JSON.stringify(bItem)) return false
  }
  return true
}

function shallowEqualTiming(a: RoundTiming, b: RoundTiming): boolean {
  return (
    (a.bettingStartedAt ?? null) === (b.bettingStartedAt ?? null) &&
    (a.bettingEndsAt ?? null) === (b.bettingEndsAt ?? null) &&
    (a.spinningStartedAt ?? null) === (b.spinningStartedAt ?? null) &&
    (a.spinningEndsAt ?? null) === (b.spinningEndsAt ?? null) &&
    (a.tricksStartedAt ?? null) === (b.tricksStartedAt ?? null) &&
    (a.tricksEndsAt ?? null) === (b.tricksEndsAt ?? null) &&
    (a.slowingStartedAt ?? null) === (b.slowingStartedAt ?? null) &&
    (a.slowingEndsAt ?? null) === (b.slowingEndsAt ?? null)
  )
}

interface ArrayDiffResult<T> {
  added: T[]
  updated: T[]
  removed: string[]
}

function arrayDiff<T>(
  prev: T[],
  next: T[],
  id: (t: T) => string
): ArrayDiffResult<T> {
  const prevMap = new Map(prev.map((t) => [id(t), t]))
  const nextMap = new Map(next.map((t) => [id(t), t]))
  const added: T[] = []
  const updated: T[] = []
  const removed: string[] = []

  for (const [k, v] of nextMap) {
    const p = prevMap.get(k)
    if (!p) added.push(v)
    else if (JSON.stringify(p) !== JSON.stringify(v)) updated.push(v)
  }
  for (const k of prevMap.keys()) {
    if (!nextMap.has(k)) removed.push(k)
  }

  return { added, updated, removed }
}
